import { ChevronDown, ChevronUp } from 'lucide-react';

import { t } from '../../lib/i18n';
import {
  StatusChip,
  documentChecks,
  documentFile,
  documentWhen,
  documentWho,
  statusTone,
} from './document-status';
import { DocumentVerificationStatus, type DocumentWithUser } from './types';

interface DocumentCardProps {
  document: DocumentWithUser;
  open: boolean;
  onToggle: () => void;
  onView: (documentId: string) => void;
  onApprove: (documentId: string) => void;
  onReject: (document: DocumentWithUser) => void;
  isBusy: boolean;
}

/**
 * Mobile queue card — `Redesign-D-Admin-Mobile`, screen «Документи».
 *
 * The phone has no room for the desktop's list-beside-panel, so the artboard
 * folds the panel into the row: tapping the header expands the same preview,
 * checks and verdict buttons underneath it. The accordion is why the mobile
 * screen can carry the full decision without a second route.
 */
export function DocumentCard({
  document,
  open,
  onToggle,
  onView,
  onApprove,
  onReject,
  isBusy,
}: DocumentCardProps) {
  const status = document.verificationStatus ?? DocumentVerificationStatus.PENDING;
  const tone = statusTone(status);
  const documentId = document._id ?? '';

  return (
    <article
      className={`overflow-hidden rounded-cf-lg border border-cf-line bg-cf-surface ${tone.stripe}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 px-3.5 py-3 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">{documentWho(document)}</span>
          <span className="block truncate text-xs text-cf-ink-subtle">
            {document.documentType} · {documentFile(document)} · {documentWhen(document)}
          </span>
        </span>
        <StatusChip status={status} />
        <span className="text-cf-ink-subtle">
          {open ? <ChevronUp size={16} strokeWidth={1.75} /> : <ChevronDown size={16} strokeWidth={1.75} />}
        </span>
      </button>

      {open && (
        <div className="flex flex-col gap-3 border-t border-cf-line-soft bg-cf-surface-sunken p-3.5">
          <DocumentPreview document={document} onView={onView} />
          <DocumentChecks document={document} />
          <DecisionButtons
            status={status}
            isBusy={isBusy}
            onApprove={() => documentId && onApprove(documentId)}
            onReject={() => onReject(document)}
          />
        </div>
      )}
    </article>
  );
}

/**
 * The 3:2 dashed preview plate. In the artboard it is inert; here it is the
 * button that opens the real scan through the presigned-URL endpoint, because
 * a verifier who cannot see the document cannot verify it.
 */
export function DocumentPreview({
  document,
  onView,
}: {
  document: DocumentWithUser;
  onView: (documentId: string) => void;
}) {
  const documentId = document._id ?? '';
  return (
    <button
      type="button"
      disabled={!documentId}
      onClick={() => documentId && onView(documentId)}
      title={t('verification.admin.docs.previewHint')}
      className="flex aspect-3/2 w-full items-center justify-center rounded-cf-md border border-dashed border-cf-line bg-cf-fill p-3 text-center font-mono text-[10px] tracking-[0.08em] text-cf-ink-subtle uppercase hover:border-cf-ink disabled:cursor-not-allowed lg:text-[11px]"
    >
      {t('verification.admin.docs.preview')} · {documentFile(document)}
    </button>
  );
}

/** The key/value check list both viewports draw under the preview. */
export function DocumentChecks({ document }: { document: DocumentWithUser }) {
  return (
    <div className="flex flex-col">
      {documentChecks(document).map((check) => (
        <div
          key={check.key}
          className="flex justify-between gap-2 border-b border-cf-line-soft py-[7px] text-[13px] lg:py-2"
        >
          <span className="text-cf-ink-muted">{check.key}</span>
          <span className={`font-mono ${check.tone}`}>{check.value}</span>
        </div>
      ))}
      {document.rejectionReason && (
        <div className="flex justify-between gap-2 border-b border-cf-line-soft py-[7px] text-[13px] lg:py-2">
          <span className="text-cf-ink-muted">
            {t('verification.admin.docs.rejectionReason')}
          </span>
          <span className="text-right font-mono text-cf-danger">{document.rejectionReason}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Approve / reject. Only a pending document offers them — the artboard draws
 * the pair on the queue's working state, and re-deciding a closed document is
 * an action the API does not accept.
 */
export function DecisionButtons({
  status,
  isBusy,
  onApprove,
  onReject,
}: {
  status: DocumentVerificationStatus;
  isBusy: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  if (status !== DocumentVerificationStatus.PENDING) return null;

  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={onApprove}
        disabled={isBusy}
        className="h-11 flex-1 rounded-cf-md bg-cf-success text-sm font-semibold text-white disabled:opacity-50"
      >
        {t('verification.admin.docs.approve')}
      </button>
      <button
        type="button"
        onClick={onReject}
        disabled={isBusy}
        className="h-11 flex-1 rounded-cf-md border border-[rgba(220,38,38,0.35)] bg-cf-surface text-sm font-semibold text-cf-danger disabled:opacity-50"
      >
        {t('verification.admin.docs.reject')}
      </button>
    </div>
  );
}
